import Submission from "../types/Submission";

// format steps with a progress bar
const workflowStepIndicator = ({ step, stepCount, stepName, steps }: any) => {
  // If we have steps, that means we should use that to determine the progress. This is currently only on the single submission page
  let adjustedStep = step;
  if (steps) {
    steps = typeof steps === "string" ? JSON.parse(steps) : steps;
    adjustedStep = getAdjustedStepNumber(step, steps);
    stepCount = steps.filter(includeInStepCount).length;
    stepName = steps[step - 1].name;
  }

  const percentage = Math.round((adjustedStep / stepCount) * 100);
  return (
    <div className="text-left font-medium sm:flex md:flex sm:flex-col-reverse md:flex-col-reverse sm:mb-2 md:mb-2">
      <div className="w-full bg-gray-200 rounded-full shadow mb-2">
        <div
          className="bg-blue-400 text-center rounded-full align-middle"
          style={{ width: percentage + "%" }}
        >
          <div className="text-white text-xs tracking-widest py-1">
            {adjustedStep + "/" + stepCount}
          </div>
        </div>
      </div>
      <div className="text-sm font-normal leading-tight sm:mb-2 md:mb-2">
        {stepName || "Unknown"}
      </div>
    </div>
  );
};

// display the correct step number by subtracting from the count the number of prior steps marked as 'excludeFromStepCount'
const getAdjustedStepNumber = (step: number, allSteps: any): number => {
  if (typeof allSteps === "number" || !allSteps) {
    return step;
  }
  let adjustment = allSteps.filter((s: any, i: number) => {
    return !includeInStepCount(s) && i < step;
  }).length;
  return step - adjustment;
};

const classNames = (...classes: any) => {
  return classes.filter(Boolean).join(" ");
};

// transform api response to the format we need to display
const formatSubmissions = (submissions: any, steps?: any) => {
  return submissions.map((submission: any): Submission => {
    return {
      fundId: submission.PK.replace("FORM#", ""),
      submissionId: submission.SK.replace("SUBMISSION#", ""),
      applicantName: submission.applicantName
        ? submission.applicantName.trim()
        : submission.SK.replace("SUBMISSION#", ""),
      victimName: submission.victimName && submission.victimName.trim(),
      applicantEmail: submission.applicantEmail,
      status: submission.status,
      step: getAdjustedStepNumber(submission.step, steps || submission.steps),
      stepCount: steps
        ? steps.filter(includeInStepCount).length
        : submission.steps
        ? submission.steps.filter(includeInStepCount).length
        : "?",
      applicationType: submission.applicationType,
      applicationTypeShort: submission.applicationType
        ? submission.applicationType.split("-")[0].trim()
        : "",
      fundName: submission.fundName,
      stepName: steps
        ? steps[submission.step - 1].name
        : submission.stepName
        ? submission.stepName
        : "Unknown",
      updatedTime: new Date(submission.updatedAt).getTime(),
      updatedDate: dateFormatter(new Date(submission.updatedAt)),
      createdTime: new Date(submission.createdAt).getTime(),
      createdDate: dateFormatter(new Date(submission.createdAt))
    };
  });
};

// include steps in step count only if they don't have a decimal or they are the first item in their group with a decimal
const includeInStepCount = (step) => {
  return !step.id.toString().includes(".") || step.id.toString().includes(".1");
};

const dateFormatter = (dt: any) => {
  return (
    [dt.getMonth() + 1, dt.getDate(), dt.getFullYear()].join("/") +
    " " +
    [
      dt.getHours() % 12 || 12,
      dt.getMinutes() < 10 ? "0" + dt.getMinutes() : dt.getMinutes()
    ].join(":") +
    (dt.getHours() >= 12 ? "PM" : "AM")
  );
};

const categoriesColors: any = {
  "Category A": "bg-green-100 text-green-800",
  "Category B": "bg-yellow-100 text-yellow-800",
  "Category C": "bg-orange-100 text-orange-800",
  "Category D": "bg-blue-100 text-blue-800",
  "Category E": "bg-indigo-100 text-indigo-800",
  "Category F": "bg-purple-100 text-purple-800"
};

// status colors
const statusColors: any = {
  "In Progress": "bg-blue-100 text-blue-800",
  Approved: "bg-green-100 text-green-800",
  "Conditional Approval": "bg-green-50 text-green-700",
  Rejected: "bg-red-100 text-red-800",
  "Deficiency Review": "bg-yellow-100 text-yellow-800"
};

// advance payment status colors
const advancePaymentStatusColors: any = {
  "In Progress": "bg-blue-100 text-blue-800",
  Pending: "bg-blue-100 text-blue-800",
  Approved: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
  Withdrawn: "bg-red-100 text-red-800",
  "Needs Review": "bg-yellow-100 text-yellow-800",
  "Not Requested": "bg-gray-100 text-gray-800",
  "Not Applicable": "bg-gray-100 text-gray-800"
};

// action badges on timeline
const actionColors: any = {
  approve: "bg-green-100 text-green-800",
  "updated by applicant": "bg-blue-100 text-blue-800",
  note: "bg-blue-100 text-blue-800",
  update: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-800",
  "admin-reject": "bg-red-100 text-red-800",
  "reviewer-reject": "bg-red-100 text-red-800",
  "deficiency review": "bg-yellow-100 text-yellow-800"
};

// action icon on the timeline
const actionIcons: any = (action: string) => {
  switch (action) {
    case "approve":
      return (
        <div className="h-8 w-8 bg-gray-100 text-green-500 rounded-full ring-8 ring-white flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      );
    case "note":
      return (
        <div className="h-8 w-8 bg-gray-100 text-blue-600 rounded-full ring-8 ring-white flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
            <path
              fillRule="evenodd"
              d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      );
    case "updated by applicant":
    case "update":
      return (
        <div className="h-8 w-8 bg-gray-100 text-blue-600 rounded-full ring-8 ring-white flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path
              fillRule="evenodd"
              d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      );
    case "delete":
    case "admin-reject":
    case "reviewer-reject":
    case "permanent-reject":
    case "rejected":
      return (
        <div className="h-8 w-8 bg-gray-100 text-red-600 rounded-full ring-8 ring-white flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      );
    case "deficiency-review":
    case "deficiency review":
      return (
        <div className="h-8 w-8 bg-gray-100 text-yellow-700 rounded-full ring-8 ring-white flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        </div>
      );
    default:
      return (
        <div className="h-8 w-8 bg-gray-100 text-gray-500 rounded-full ring-8 ring-white flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      );
  }
};

function IsJsonString(str: any) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export {
  categoriesColors,
  statusColors,
  advancePaymentStatusColors,
  actionIcons,
  actionColors,
  workflowStepIndicator,
  dateFormatter,
  getAdjustedStepNumber,
  formatSubmissions,
  IsJsonString,
  classNames
};
